import React from 'react';
import {
  View, Text, StyleSheet, FlexStyle,
} from 'react-native';
import Svg, { Circle, G } from 'react-native-svg';
import SpaceUtilizationWidget from './SpaceUtilizationWidget';
import OccupiedTimeIcon from '../../../16pxIcons/OccupiedTimeIcon';
import Style from '../../../../constants/Style';
import Colors from '../../../../constants/Colors';
import { ChartDataPoint } from '../../../../graphs/axisUtils';

interface OccupiedTimeProps {
  data: ChartDataPoint[],
  comparisonData?: ChartDataPoint[],
}

interface OccupiedTimeInfoProps {
  data: ChartDataPoint[],
  style?: FlexStyle | FlexStyle[],
}

const OccupiedTimeInfo = ({ data, style }: OccupiedTimeInfoProps) => {
  const pieWidth: number = 76;
  const totalHours: number = data.length;
  const occupiedHours: number = data.filter((item: ChartDataPoint) => item.value > 0).length;
  const unoccupiedHours: number = totalHours - occupiedHours;
  const percentage: number = (occupiedHours / totalHours) * 100;
  const dataValid: boolean = percentage >= 0 && data.length > 0;
  const radius: number = pieWidth / 2;
  const strokeWidth: number = 12;
  const strokeDasharray: number = Math.PI * pieWidth;

  return <View style={[styles.container, style]}>
    <View style={styles.countSection}>
      <Text style={styles.count}>{totalHours}</Text>
      <Text style={styles.hoursLabel}>{'Total hours'}</Text>
    </View>
    <Svg style={{ width: pieWidth + strokeWidth, height: pieWidth + strokeWidth }}>
      <G
        translate={[radius + strokeWidth / 2, radius + strokeWidth / 2]}
      >
        {dataValid ? <>
          <Circle
            r={radius}
            fill='transparent'
            {...{ strokeWidth, strokeDasharray }}
            stroke={Colors.light.hereworksYellow500}
            strokeDashoffset={(1 - (percentage / 100)) * strokeDasharray}
          />
          <Circle
            rotation={(360 * percentage) / 100}
            r={radius}
            fill='transparent'
            {...{ strokeWidth, strokeDasharray }}
            stroke={Colors.light.hereworksRed500}
            strokeDashoffset={(percentage / 100) * strokeDasharray}
          />
        </> : <Circle
          r={radius}
          fill='transparent'
          {...{ strokeWidth, strokeDasharray }}
          stroke={Colors.light.hereworksPrimaryGrey600}
          strokeDashoffset={0}
        />}
      </G>
    </Svg>
    <View style={styles.legend}>
      <View style={styles.legendSection}>
        <View style={Style.row}>
          <View style={styles.square} />
          <Text style={styles.label}>{'Unoccupied time'}</Text>
          {unoccupiedHours >= 0
            && <Text style={styles.time}>{`${(unoccupiedHours)}h`}</Text>}
        </View>
        <Text style={styles.time}>
          {`${dataValid ? 100 - Math.floor(percentage) : '-'}%`}
        </Text>
      </View>
      <View style={styles.legendSection}>
        <View style={Style.row}>
          <View style={[styles.square, styles.squareOccupied]} />
          <Text style={styles.label}>{'Occupied time'}</Text>
          {occupiedHours >= 0 && <Text style={styles.time}>{`${occupiedHours}h`}</Text>}
        </View>
        <Text style={styles.time}>
          {`${dataValid ? Math.floor(percentage) : '-'}%`}
        </Text>
      </View>
    </View>
  </View>;
};

const OccupiedTimeChart = ({ data, comparisonData }: OccupiedTimeProps) => <SpaceUtilizationWidget
  row
  icon={<OccupiedTimeIcon />}
  title='Occupied Time'
  content={<View>
    <OccupiedTimeInfo {...{ data }} />
    {comparisonData?.length > 0 && <OccupiedTimeInfo
      data={comparisonData}
      style={styles.comparisonSection}
    />}
  </View>}
/>;

const styles = StyleSheet.create({
  container: {
    ...Style.row,
    flex: 1,
    marginStart: 68,
  },
  countSection: {
    marginEnd: 16,
    flex: 1,
    alignItems: 'center',
  },
  count: {
    fontSize: 30,
    lineHeight: 30,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
    marginBottom: 3,
  },
  hoursLabel: {
    fontSize: 16,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  legend: {
    flex: 1,
    marginStart: 32,
  },
  legendSection: {
    ...Style.row,
    marginVertical: 2,
  },
  label: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
    marginHorizontal: 8,
  },
  time: {
    fontSize: 12,
    lineHeight: 20,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlack600,
  },
  square: {
    width: 16,
    height: 16,
    borderRadius: 2,
    backgroundColor: Colors.light.hereworksRed500,
  },
  squareOccupied: {
    backgroundColor: Colors.light.hereworksYellow500,
  },
  comparisonSection: {
    marginTop: 20,
    borderTopWidth: 1,
    borderTopColor: Colors.light.hereworksWhite500,
    paddingTop: 16,
  },
});

export default OccupiedTimeChart;
